import { useMemo } from "react";
import {
  CurrentOperation,
  Role,
  Member,
  UpdatePermissionsRequest,
} from "../models";
import { CurrentOperationWithDerivedState } from "../models/operations";
import { StateEnveloped } from "../redux/helpers";
import { makeHookDeprecationWarn } from "../helpers/warnings";
import { CreateFieldEnrollmentRequest } from "../models/enrollments";
import { BatchInvite } from "../models/invitations";

import {
  useBasicFarmsData,
  useEnrollmentsData,
  useFieldCropsData,
  useFieldEnrollmentsData,
  useFieldsData,
  useMembersData,
  useInvitesData,
  useCurrentOperationData,
  useEnrollFields,
  useRemoveMember,
  useRemoveCortevaMembers,
  useUpdateMemberPermissions,
  useInviteMember,
  useInviteMembers,
  useUnInviteMember,
  useUpdateOperation,
  useDeleteOperation,
  useCurrentOperationGlobalActivity,
  useCurrentOperationGlobalStatus,
} from "../next/hooks";

export type CurrentOperationHookResult =
  StateEnveloped<CurrentOperationWithDerivedState> & {
    modifiers: {
      update: (operation: CurrentOperation) => Promise<void>;
      delete: () => Promise<void>;
      inviteMember: (
        email: string,
        role: Role,
        inviteLinkUrl?: string,
      ) => Promise<void>;
      inviteMembers: (invites: BatchInvite[]) => Promise<BatchInvite[]>;
      unInviteMember: (email: string) => Promise<void>;
      removeCortevaMembers: (
        copyOperation: boolean,
        removalReason: string,
      ) => Promise<void>;
      deleteMember: (member: Member) => Promise<void>;
      updateMemberPermissions: (
        member: Member,
        permissions: UpdatePermissionsRequest,
      ) => Promise<void>;
      enrollFields: (
        createFieldEnrollmentRequest: CreateFieldEnrollmentRequest,
      ) => Promise<void>;
    };
  };
export type CurrentOperationHook = () => CurrentOperationHookResult;

const currentOperationDataApplicationPermissionsDeprecationWarn =
  makeHookDeprecationWarn("useCurrentOperation().data.applicationPermissions");
const currentOperationDataIdDeprecationWarn = makeHookDeprecationWarn(
  "useCurrentOperation().data.id",
);
const currentOperationDataNameDeprecationWarn = makeHookDeprecationWarn(
  "useCurrentOperation().data.name",
);
const currentOperationDataPermissionsDeprecationWarn = makeHookDeprecationWarn(
  "useCurrentOperation().data.permissions",
);
const currentOperationDataSettingsDeprecationWarn = makeHookDeprecationWarn(
  "useCurrentOperation().data.settings",
);
const currentOperationDataEnrollmentsDeprecationWarn = makeHookDeprecationWarn(
  "useCurrentOperation().data.enrollments",
  "useEnrollments",
);
const currentOperationDataEnrollmentsByIdDeprecationWarn =
  makeHookDeprecationWarn(
    "useCurrentOperation().data.enrollmentsById",
    "useEnrollments",
  );
const currentOperationDataFarmsDeprecationWarn = makeHookDeprecationWarn(
  "useCurrentOperation().data.farms",
  "useFarms",
);
const currentOperationDataFarmsByIdDeprecationWarn = makeHookDeprecationWarn(
  "useCurrentOperation().data.farmsById",
  "useFarms",
);
const currentOperationDataFieldEnrollmentsDeprecationWarn =
  makeHookDeprecationWarn(
    "useCurrentOperation().data.fieldsEnrollments",
    "useFieldEnrollments",
  );
const currentOperationDataFieldToCropMappingDeprecationWarn =
  makeHookDeprecationWarn(
    "useCurrentOperation().data.fieldToCropMapping",
    "useFieldCrops",
  );
const currentOperationDataFieldsDeprecationWarn = makeHookDeprecationWarn(
  "useCurrentOperation().data.fields",
  "useFields",
);
const currentOperationDataFieldsByIdDeprecationWarn = makeHookDeprecationWarn(
  "useCurrentOperation().data.fieldsById",
  "useFields",
);
const currentOperationDataMembersDeprecationWarn = makeHookDeprecationWarn(
  "useCurrentOperation().data.members",
  "useMembers",
);
const currentOperationDataMembersByGranularIdDeprecationWarn =
  makeHookDeprecationWarn(
    "useCurrentOperation().data.membersByGranularId",
    "useMembers",
  );
const currentOperationPendingInvitationsDeprecationWarn =
  makeHookDeprecationWarn(
    "useCurrentOperation().data.pendingInvitations",
    "useInvites",
  );

const currentOperationUpdateDeprecationWarn = makeHookDeprecationWarn(
  "useCurrentOperation().modifiers.update",
  "useUpdateOperation",
);
const currentOperationDeleteDeprecationWarn = makeHookDeprecationWarn(
  "useCurrentOperation().modifiers.delete",
  "useDeleteOperation",
);
const currentOperationEnrollFieldsDeprecationWarn = makeHookDeprecationWarn(
  "useCurrentOperation().modifiers.enrollFields",
  "useEnrollFields",
);
const currentOperationDeleteMemberDeprecationWarn = makeHookDeprecationWarn(
  "useCurrentOperation().modifiers.deleteMember",
  "useRemoveMember",
);
const currentOperationRemoveCortevaMembersDeprecationWarn =
  makeHookDeprecationWarn(
    "useCurrentOperation().modifiers.removeCortevaMembers",
    "useRemoveCortevaMembers",
  );
const currentOperationUpdateMemberPermissionsDeprecationWarn =
  makeHookDeprecationWarn(
    "useCurrentOperation().modifiers.updateMemberPermissions",
    "useUpdateMemberPermissions",
  );
const currentOperationInviteMemberDeprecationWarn = makeHookDeprecationWarn(
  "useCurrentOperation().modifiers.inviteMember",
  "useInviteMember",
);
const currentOperationInviteMembersDeprecationWarn = makeHookDeprecationWarn(
  "useCurrentOperation().modifiers.inviteMembers",
  "useInviteMembers",
);
const currentOperationUnInviteMemberDeprecationWarn = makeHookDeprecationWarn(
  "useCurrentOperation().modifiers.unInviteMember",
  "useUnInviteMember",
);

const Hook: CurrentOperationHook = () => {
  const { farms, farmsById } = useBasicFarmsData();
  const { enrollments, enrollmentsById } = useEnrollmentsData();
  const { fieldEnrollmentsByFeature: fieldsEnrollments } =
    useFieldEnrollmentsData();
  const { fields, fieldsById } = useFieldsData();
  const { members, membersByGranularId } = useMembersData();
  const { pendingInvitations } = useInvitesData();

  const { fieldCropsByField: fieldToCropMapping } = useFieldCropsData();

  const currentOperationData = useCurrentOperationData();
  const { mutate: updateOperation } = useUpdateOperation();
  const { mutate: deleteOperation } = useDeleteOperation();

  const { mutate: enrollFields } = useEnrollFields();
  const { mutate: removeMember } = useRemoveMember();
  const { mutate: removeCortevaMembers } = useRemoveCortevaMembers();
  const { mutate: updateMemberPermissions } = useUpdateMemberPermissions();
  const { mutate: inviteMember } = useInviteMember();
  const { mutate: inviteMembers } = useInviteMembers();
  const { mutate: unInviteMember } = useUnInviteMember();

  const status = useCurrentOperationGlobalStatus();
  const activity = useCurrentOperationGlobalActivity();

  const currentOperationId = currentOperationData.id;

  const data = useMemo(
    () => ({
      get applicationPermissions() {
        currentOperationDataApplicationPermissionsDeprecationWarn();
        return currentOperationData.applicationPermissions;
      },
      get user_permission() {
        return currentOperationData.user_permission;
      },
      get id() {
        currentOperationDataIdDeprecationWarn();
        return currentOperationData.id;
      },
      get name() {
        currentOperationDataNameDeprecationWarn();
        return currentOperationData.name;
      },
      get permissions() {
        currentOperationDataPermissionsDeprecationWarn();
        return currentOperationData.permissions;
      },
      get settings() {
        currentOperationDataSettingsDeprecationWarn();
        return currentOperationData.settings;
      },

      get enrollments() {
        currentOperationDataEnrollmentsDeprecationWarn();
        return enrollments;
      },
      get fieldsEnrollments() {
        currentOperationDataFieldEnrollmentsDeprecationWarn();
        return fieldsEnrollments;
      },
      get enrollmentsById() {
        currentOperationDataEnrollmentsByIdDeprecationWarn();
        return enrollmentsById;
      },
      get fieldToCropMapping() {
        currentOperationDataFieldToCropMappingDeprecationWarn();
        return fieldToCropMapping;
      },
      get fields() {
        currentOperationDataFieldsDeprecationWarn();
        return fields;
      },
      get fieldsById() {
        currentOperationDataFieldsByIdDeprecationWarn();
        return fieldsById;
      },
      get farms() {
        currentOperationDataFarmsDeprecationWarn();
        return farms;
      },
      get farmsById() {
        currentOperationDataFarmsByIdDeprecationWarn();
        return farmsById;
      },
      get members() {
        currentOperationDataMembersDeprecationWarn();
        return members;
      },
      get membersByGranularId() {
        currentOperationDataMembersByGranularIdDeprecationWarn();
        return membersByGranularId;
      },
      get pendingInvitations() {
        currentOperationPendingInvitationsDeprecationWarn();
        return pendingInvitations;
      },
    }),
    [
      currentOperationData,
      enrollments,
      fields,
      fieldsById,
      fieldsEnrollments,
      fieldToCropMapping,
      farms,
      farmsById,
      members,
      membersByGranularId,
      enrollmentsById,
      pendingInvitations,
    ],
  );

  const modifiers = useMemo(
    () => ({
      update: async (operation: CurrentOperation): Promise<void> => {
        currentOperationUpdateDeprecationWarn();
        await updateOperation(operation);
      },
      delete: async (): Promise<void> => {
        currentOperationDeleteDeprecationWarn();
        await deleteOperation(currentOperationId);
      },
      inviteMember: async (
        withPhoneOrEmail: string,
        as: Role,
        inviteLinkUrl?: string,
      ): Promise<void> => {
        currentOperationInviteMemberDeprecationWarn();
        await inviteMember({
          withPhoneOrEmail,
          as,
          inviteLinkUrl,
        });
      },
      inviteMembers: async (invites: BatchInvite[]): Promise<BatchInvite[]> => {
        currentOperationInviteMembersDeprecationWarn();
        return await inviteMembers({
          invites,
        });
      },
      unInviteMember: async (emailAddressOrPhone: string): Promise<void> => {
        currentOperationUnInviteMemberDeprecationWarn();
        await unInviteMember({ emailAddressOrPhone });
      },
      removeCortevaMembers: async (
        copyOperation: boolean,
        removalReason: string,
      ): Promise<void> => {
        currentOperationRemoveCortevaMembersDeprecationWarn();
        await removeCortevaMembers({
          copyOperation,
          removalReason,
        });
      },
      deleteMember: async (member: Member): Promise<void> => {
        currentOperationDeleteMemberDeprecationWarn();
        await removeMember(member.granularId);
      },
      updateMemberPermissions: async (
        member: Member,
        permissions: UpdatePermissionsRequest,
      ): Promise<void> => {
        currentOperationUpdateMemberPermissionsDeprecationWarn();
        await updateMemberPermissions({
          member,
          permissions,
        });
      },
      enrollFields: async (
        createFieldEnrollmentRequest: CreateFieldEnrollmentRequest,
      ): Promise<void> => {
        currentOperationEnrollFieldsDeprecationWarn();
        await enrollFields(createFieldEnrollmentRequest);
      },
    }),
    [
      currentOperationId,
      updateOperation,
      deleteOperation,
      enrollFields,
      removeMember,
      removeCortevaMembers,
      updateMemberPermissions,
      inviteMember,
      inviteMembers,
      unInviteMember,
    ],
  );

  const memoizedResult = useMemo(
    () => ({
      status,
      activity,
      data,
      modifiers,
    }),
    [status, activity, data, modifiers],
  );

  return memoizedResult;
};

export default Hook;
